import { useEffect, useState } from "react";
import { Avatar, Input, Select } from "antd";
import { useSelector } from "react-redux";
import {
  FaCoins,
  FaEdit,
  FaSave,
  FaMoneyBillWave,
  FaUser,
  FaPhone,
  FaTag,
  FaComments,
} from "react-icons/fa";
import { RedeemModal } from "../../../components/alert/allertbox";
import BankManagement from "../bank/BankManagement";
import {
  useGetConsultantProfileQuery,
  useUpdateConsultantMutation,
} from "../../../redux/rtk/apiRtk";
import { primaryColor } from "../../../utils/constants";
import { uploadImageToCloudinary } from "../../../utils/imageUpload";
import { UploadOutlined, UserOutlined } from "@ant-design/icons";
import { ImageUploadModel } from "../../../components/model/ImageModel";
import { consultantCategory } from "../../../components/consultantCategory";

const { Option } = Select;

function Profile() {
  const { data, isLoading, refetch } = useGetConsultantProfileQuery();
  const [updateConsultant] = useUpdateConsultantMutation();
  const { loading } = useSelector((state) => state.user);

  const [editable, setEditable] = useState(false);
  const [formData, setFormData] = useState();
  const [animateProfile, setAnimateProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data, isLoading]);

  useEffect(() => {
    // Trigger animation after component mounts
    setTimeout(() => {
      setAnimateProfile(true);
    }, 100);
  }, []);

  const handleEditClick = () => {
    setEditable(true);
  };

  const handleSaveClick = async () => {
    setSaving(true);

    let imageUrl = formData?.image;

    // Upload image if a new one was selected
    if (image) {
      try {
        // You'll need to import uploadImageToCloudinary or implement your image upload logic
        // For now, we'll use the preview URL
        const url = await uploadImageToCloudinary(image);
        imageUrl = url;
      } catch (error) {
        console.error("Error uploading image:", error);
      }
    }

    const body = {
      about: formData.about,
      pricePerMinute: formData.pricePerMinute,
      availability: formData.availability,
      communicationType: formData.communicationType,
      image: imageUrl,
      firstName: formData?.firstName,
      lastName: formData?.lastName,
      phone: formData?.phone,
      category: formData.category,
    };

    try {
      const data = await updateConsultant(body).unwrap();
      console.log("Total earnings", data);

      // Reset image states
      setImage(null);
      setImagePreview(null);

      setTimeout(() => {
        setSaving(false);
        setEditable(false);
      }, 600);
    } catch (error) {
      console.error(error);
      setSaving(false);
    }
  };

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const [showAlert, setShowAlert] = useState(false);

  const handleCancel = () => {
    setShowAlert(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div
            className="w-16 h-16 rounded-full border-4 border-t-transparent animate-spin"
            style={{
              borderColor: `${primaryColor} transparent transparent transparent`,
            }}
          ></div>
          <p
            className="mt-4 text-lg font-medium"
            style={{ color: primaryColor }}
          >
            Loading profile...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showAlert && (
        <RedeemModal
          isOpen={showAlert}
          onClose={handleCancel}
          onRedeem={refetch}
          fullAmount={formData?.wallet}
        />
      )}

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-6 md:py-8  sm:px-6 lg:px-8">
        {/* Profile Header */}
        <div
          className={`relative overflow-hidden rounded-2xl shadow-lg transition-all duration-700 transform ${animateProfile
            ? "translate-y-0 opacity-100"
            : "translate-y-10 opacity-0"
            }`}
          style={{
            background: `linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)`,
            boxShadow: "0 10px 40px rgba(164, 1, 101, 0.1)",
          }}
        >
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-pink-100 to-transparent rounded-bl-full opacity-70"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-pink-100 to-transparent rounded-tr-full opacity-70"></div>
          <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-gradient-to-tl from-pink-200 to-transparent rounded-tl-full opacity-50"></div>

          <div className="relative z-10 p-6 sm:p-8">
            <div className="flex flex-col md:flex-row items-center md:items-start justify-between">
              {/* Left: Profile Image */}
              {editable ? (
                <div
                  className="relative cursor-pointer group"
                  onClick={() => setShowImageModal(true)}
                >
                  <Avatar
                    size={{ xs: 80, sm: 100, md: 110, lg: 110, xl: 110, xxl: 110 }}
                    src={imagePreview || formData?.image}
                    icon={
                      !imagePreview &&
                      !formData?.image && (
                        <UserOutlined style={{ color: "#888" }} />
                      )
                    }
                    className="border-3 border-white shadow-md transition-all duration-300 group-hover:shadow-xl"
                    style={{
                      boxShadow: `0 5px 15px rgba(164, 1, 101, 0.1)`,
                    }}
                  />
                  <div
                    className="absolute inset-0 flex items-center justify-center rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100"
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.4)",
                      zIndex: 5,
                    }}
                  >
                    <UploadOutlined
                      style={{
                        fontSize: "28px",
                        color: "white",
                      }}
                    />
                  </div>
                </div>
              ) : (
                <Avatar
                  size={110}
                  src={imagePreview || formData?.image}
                  icon={
                    !imagePreview &&
                    !formData?.image && (
                      <UserOutlined style={{ color: "#888" }} />
                    )
                  }
                  className="border-3 border-white shadow-md"
                  style={{ boxShadow: `0 5px 15px rgba(164, 1, 101, 0.1)` }}
                />
              )}

              {/* Middle: Name and Category Chips */}
              <div className="md:ml-6 flex-1 mt-4 md:mt-0 text-center md:text-left w-full">
                <h2
                  className="text-2xl font-bold mb-3 transition-all duration-300"
                  style={{ color: primaryColor }}
                >
                  {formData?.firstName} {formData?.lastName}
                </h2>

                <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-4">
                  {formData?.category?.length > 0 ? (
                    formData.category.map((cat, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 rounded-full text-sm font-medium capitalize transition-all duration-300 transform hover:scale-105"
                        style={{
                          backgroundColor: `${primaryColor}15`,
                          color: primaryColor,
                          boxShadow: "0 2px 5px rgba(164, 1, 101, 0.1)",
                        }}
                      >
                        {cat}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500">
                      No categories specified
                    </span>
                  )}
                </div>

                <div className="flex items-center justify-center md:justify-start mt-4">
                  <div className="flex items-center">
                    <div
                      className="p-2 rounded-full"
                      style={{ background: `${primaryColor}20` }}
                    >
                      <FaCoins className="text-yellow-500" size={22} />
                    </div>
                    <div className="ml-2 text-left">
                      <p className="text-xs text-gray-500 font-medium">
                        Wallet Balance
                      </p>
                      <p
                        className="text-lg font-bold"
                        style={{ color: primaryColor }}
                      >
                        ₹{" "}
                        {formData?.wallet
                          ? parseFloat(formData?.wallet).toFixed(2)
                          : 0}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Edit and Redeem Buttons */}
              <div className="mt-6 md:mt-0 md:ml-4 self-center md:self-start flex flex-col space-y-3 w-full md:w-auto">
                {editable ? (
                  <button
                    className={`flex items-center justify-center px-4 py-2 rounded-lg shadow-md transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${saving ? "opacity-70" : ""
                      }`}
                    onClick={handleSaveClick}
                    disabled={saving}
                    style={{
                      background: primaryColor,
                      color: "white",
                      boxShadow: `0 4px 14px 0 rgba(164, 1, 101, 0.25)`,
                    }}
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="mr-2" size={14} />
                        Save Changes
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    className="flex items-center justify-center px-4 py-2 rounded-lg shadow-md transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98]"
                    onClick={handleEditClick}
                    style={{
                      background: primaryColor,
                      color: "white",
                      boxShadow: `0 4px 14px 0 rgba(164, 1, 101, 0.25)`,
                    }}
                  >
                    <FaEdit className="mr-2" size={14} />
                    Edit Profile
                  </button>
                )}

                <button
                  disabled={!(formData?.wallet >= 0)}
                  onClick={() => setShowAlert(true)}
                  className={`flex items-center justify-center px-4 py-2 rounded-lg shadow-md transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${formData?.wallet >= 0
                    ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                    }`}
                >
                  <FaMoneyBillWave className="mr-2" size={14} />
                  Redeem Funds
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Information */}
        <div
          className={`mt-8 bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-700 delay-100 transform ${animateProfile
            ? "translate-y-0 opacity-100"
            : "translate-y-10 opacity-0"
            }`}
          style={{ boxShadow: "0 10px 40px rgba(164, 1, 101, 0.1)" }}
        >
          <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center">
              <div
                className="p-2 rounded-full mr-3"
                style={{ background: `${primaryColor}15` }}
              >
                <FaUser className="text-lg" style={{ color: primaryColor }} />
              </div>
              <h3
                className="text-xl font-semibold"
                style={{ color: primaryColor }}
              >
                Profile Information
              </h3>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {/* About Section */}
              <div className="md:col-span-2 mb-6">
                <div className="flex items-center mb-3">
                  <div
                    className="p-2 rounded-full mr-3"
                    style={{
                      background: `linear-gradient(135deg, ${primaryColor}30, ${primaryColor}10)`,
                    }}
                  >
                    <FaUser
                      className="text-lg"
                      style={{ color: primaryColor }}
                    />
                  </div>
                  <h4 className="font-medium text-gray-700">About Me</h4>
                </div>
                {editable ? (
                  <Input.TextArea
                    value={formData?.about}
                    onChange={(e) => handleChange("about", e.target.value)}
                    className="w-full rounded-lg border-gray-300 focus:border-transparent focus:ring-2 transition-all duration-300"
                    style={{
                      minHeight: "120px",
                      "--tw-ring-color": primaryColor,
                    }}
                  />
                ) : (
                  <div className="bg-gray-50 p-5 rounded-xl border border-gray-100 shadow-sm">
                    <p className="text-gray-600 leading-relaxed">
                      {formData?.about || "No information provided yet."}
                    </p>
                  </div>
                )}
              </div>

              {/* Personal Information */}
              <div className="space-y-6">
                <div className="flex items-center justify-between p-5 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-md transform hover:-translate-y-1">
                  <div className="flex items-center w-full">
                    <div
                      className="p-3 rounded-full"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor}30, ${primaryColor}10)`,
                      }}
                    >
                      <FaUser
                        className="text-lg"
                        style={{ color: primaryColor }}
                      />
                    </div>
                    <div className="ml-3 w-full">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                        First Name
                      </p>
                      {editable ? (
                        <Input
                          value={formData?.firstName}
                          onChange={(e) =>
                            handleChange("firstName", e.target.value)
                          }
                          className="w-full border-gray-300 rounded-md focus:border-transparent focus:ring-2 transition-all duration-300 mt-1"
                          style={{ "--tw-ring-color": primaryColor }}
                        />
                      ) : (
                        <p className="font-medium text-lg">
                          {formData?.firstName || "Not provided"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-5 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-md transform hover:-translate-y-1">
                  <div className="flex items-center w-full">
                    <div
                      className="p-3 rounded-full"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor}30, ${primaryColor}10)`,
                      }}
                    >
                      <FaUser
                        className="text-lg"
                        style={{ color: primaryColor }}
                      />
                    </div>
                    <div className="ml-3 w-full">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                        Last Name
                      </p>
                      {editable ? (
                        <Input
                          value={formData?.lastName}
                          onChange={(e) =>
                            handleChange("lastName", e.target.value)
                          }
                          className="w-full border-gray-300 rounded-md focus:border-transparent focus:ring-2 transition-all duration-300 mt-1"
                          style={{ "--tw-ring-color": primaryColor }}
                        />
                      ) : (
                        <p className="font-medium text-lg">
                          {formData?.lastName || "Not provided"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-5 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-md transform hover:-translate-y-1">
                  <div className="flex items-center w-full">
                    <div
                      className="p-3 rounded-full"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor}30, ${primaryColor}10)`,
                      }}
                    >
                      <FaPhone
                        className="text-lg"
                        style={{ color: primaryColor }}
                      />
                    </div>
                    <div className="ml-3 w-full">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                        Phone
                      </p>
                      {editable ? (
                        <Input
                          value={formData?.phone}
                          onChange={(e) =>
                            handleChange("phone", e.target.value)
                          }
                          className="w-full border-gray-300 rounded-md focus:border-transparent focus:ring-2 transition-all duration-300 mt-1"
                          style={{ "--tw-ring-color": primaryColor }}
                        />
                      ) : (
                        <p className="font-medium text-lg">
                          {formData?.phone || "Not provided"}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div className="space-y-6">
                <div className="flex items-center justify-between p-5 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-md transform hover:-translate-y-1">
                  <div className="flex items-center flex-1">
                    <div
                      className="p-3 rounded-full"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor}30, ${primaryColor}10)`,
                      }}
                    >
                      <FaCoins
                        className="text-lg"
                        style={{ color: primaryColor }}
                      />
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                        Price Per Minute
                      </p>
                      {editable ? (
                        <Input
                          type="number"
                          value={formData?.pricePerMinute}
                          onChange={(e) => {
                            const value = Math.max(
                              0,
                              parseInt(e.target.value) || 0
                            );
                            handleChange("pricePerMinute", value);
                          }}
                          className="w-full border-gray-300 rounded-md focus:border-transparent focus:ring-2 transition-all duration-300 mt-2"
                          style={{ "--tw-ring-color": primaryColor }}
                        />
                      ) : (
                        <div className="mt-1">
                          <span
                            className="px-3 py-1 rounded-full text-sm font-medium inline-flex items-center"
                            style={{
                              backgroundColor: `${primaryColor}15`,
                              color: primaryColor,
                              boxShadow: "0 2px 5px rgba(164, 1, 101, 0.1)",
                            }}
                          >
                            <FaCoins className="mr-1" size={12} />₹{" "}
                            {formData?.pricePerMinute || "0"}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-5 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-md transform hover:-translate-y-1">
                  <div className="flex items-center w-full">
                    <div
                      className="p-3 rounded-full"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor}30, ${primaryColor}10)`,
                      }}
                    >
                      <FaTag
                        className="text-lg"
                        style={{ color: primaryColor }}
                      />
                    </div>
                    <div className="ml-3 w-full">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                        Categories
                      </p>
                      {editable ? (
                        <Select
                          mode="multiple"
                          style={{ width: "100%" }}
                          value={formData?.category}
                          onChange={(value) => handleChange("category", value)}
                          className="rounded-md mt-2"
                          dropdownStyle={{ borderRadius: "0.5rem" }}
                        >
                          {consultantCategory?.map((item, index) => (
                            <Option key={index} value={item.value}>
                              <div className="flex items-center">
                                <span>{item.title}</span>
                              </div>
                            </Option>
                          ))}
                        </Select>
                      ) : (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {formData?.category?.length > 0 ? (
                            formData.category.map((cat, index) => (
                              <span
                                key={index}
                                className="px-3 py-1 rounded-full text-sm font-medium capitalize transition-all duration-300 transform hover:scale-105"
                                style={{
                                  backgroundColor: `${primaryColor}15`,
                                  color: primaryColor,
                                  boxShadow: "0 2px 5px rgba(164, 1, 101, 0.1)",
                                }}
                              >
                                {cat}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-500">
                              No categories specified
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between p-5 rounded-xl bg-white border border-gray-100 shadow-sm transition-all duration-300 hover:shadow-md transform hover:-translate-y-1">
                  <div className="flex items-center flex-1">
                    <div
                      className="p-3 rounded-full"
                      style={{
                        background: `linear-gradient(135deg, ${primaryColor}30, ${primaryColor}10)`,
                      }}
                    >
                      <FaComments
                        className="text-lg"
                        style={{ color: primaryColor }}
                      />
                    </div>
                    <div className="ml-3 flex-1">
                      <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                        Communication Type
                      </p>
                      {editable ? (
                        <Select
                          mode="multiple"
                          style={{ width: "100%" }}
                          value={formData?.communicationType}
                          onChange={(value) =>
                            handleChange("communicationType", value)
                          }
                          className="rounded-md mt-2"
                          dropdownStyle={{ borderRadius: "0.5rem" }}
                        >
                          <Option value="phone">Phone</Option>
                          <Option value="chat">Chat</Option>
                        </Select>
                      ) : (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {formData?.communicationType?.length > 0 ? (
                            formData.communicationType.map((type, index) => (
                              <span
                                key={index}
                                className="px-3 py-1 rounded-full text-sm font-medium capitalize transition-all duration-300 transform hover:scale-105"
                                style={{
                                  backgroundColor: `${primaryColor}15`,
                                  color: primaryColor,
                                  boxShadow: "0 2px 5px rgba(164, 1, 101, 0.1)",
                                }}
                              >
                                {type}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-500">
                              No communication types specified
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <BankManagement />
      </div>
      <ImageUploadModel
        setShowImageModal={setShowImageModal}
        showImageModal={showImageModal}
        setImagePreview={setImagePreview}
        onSelectImage={(e) => {
          setImage(e);
        }}
      />
    </>
  );
}

export default Profile;
